import {
  ArdoqOidMapping,
  ComponentKeyMapping,
  ComponentMapping,
  ComponentTypeMapping,
  CustomIdMapping,
  DescriptionMapping,
  DisplayTextMapping,
  FieldMapping,
  PathParentMapping,
  PathReferenceFormat,
  MissingComponentsStrategy,
  IntegrationReferenceDirection,
  ReferenceMapping,
  ReferenceTypeMapping,
  RootReferenceMapping,
  TagsMapping,
  TargetReferenceMapping,
  MissingParentComponentsStrategy,
  ComponentPathMapping,
  ComponentPathMissingComponentsStrategy,
} from '@ardoq/api-types/integrations';
import { ColumnMapping } from '../transferConfigs/types';
import { DefaultFieldOptions, TableMappingMap } from './types';
import { getDefaultWorkspaceField, getNextHierarchyLevel } from './utils';

type IndexFree<T extends ColumnMapping> = Omit<T, 'index'>;

export const getDefaultColumnMapping = ({
  columnType,
  columnName,
  tableMappingMap,
  allFields,
  isCreationDisabled,
}: {
  columnType: ColumnMapping['columnType'];
  columnName: string;
  tableMappingMap: TableMappingMap;
  allFields: DefaultFieldOptions;
  isCreationDisabled: boolean;
}) => {
  switch (columnType) {
    case 'ardoq-oid':
      return defaultArdoqOidMapping();
    case 'component-key':
      return defaultComponentKeyMapping();
    case 'component':
      return defaultComponentMapping({
        tableMappingMap,
      });
    case 'component-path':
      return defaultComponentPathMapping();
    case 'component-type':
      return defaultComponentTypeMapping();
    case 'custom-id':
      return defaultCustomIdMapping({
        columnName,
        tableMappingMap,
      });
    case 'description':
      return defaultDescriptionMapping();
    case 'display-text':
      return defaultDisplayTextMapping();
    case 'field':
      return defaultFieldMapping({ columnName, allFields, isCreationDisabled });
    case 'parent':
      return defaultParentMapping();
    case 'reference':
      return defaultReferenceMapping();
    case 'reference-type':
      return defaultReferenceTypeMapping();
    case 'root-reference':
      return defaultRootReferenceMapping();
    case 'tags':
      return defaultTagsMapping();
    case 'target-reference':
      return defaultTargetReferenceMapping();
  }
};

export const defaultComponentMapping = ({
  tableMappingMap,
}: {
  tableMappingMap: TableMappingMap;
}): IndexFree<ComponentMapping> => {
  return {
    columnType: 'component',
    hierarchyLevel: getNextHierarchyLevel(tableMappingMap),
  };
};

const defaultComponentPathMapping = (): IndexFree<ComponentPathMapping> => ({
  columnType: 'component-path',
  componentTypes: [],
  pathSeparator: '::',
  missingComponents: ComponentPathMissingComponentsStrategy.ERROR,
});

const defaultCustomIdMapping = ({
  columnName,
  tableMappingMap,
}: {
  columnName: string;
  tableMappingMap: TableMappingMap;
}): IndexFree<CustomIdMapping> => {
  getDefaultWorkspaceField({
    columnName,
    tableMappingMap,
  });
  return {
    columnType: 'custom-id',
    fieldName: null,
    fieldLabel: '',
    duplicates: 'error',
  };
};

const defaultReferenceMapping = (): IndexFree<
  PathReferenceFormat & ReferenceMapping
> => {
  return {
    columnType: 'reference',
    referencePathSeparator: '::',
    referenceItemSeparator: ',',
    referenceFormat: 'path',
    referenceDirection: IntegrationReferenceDirection.OUTGOING,
    missingComponents: MissingComponentsStrategy.ERROR,
  };
};

const defaultFieldMapping = ({
  columnName,
  allFields,
  isCreationDisabled,
}: {
  columnName: string;
  allFields: DefaultFieldOptions;
  isCreationDisabled: boolean;
}): IndexFree<FieldMapping> => {
  const existingField = allFields.find(
    ({ label }) => label.toLowerCase() === columnName.trim().toLowerCase()
  );

  if (existingField) {
    if (!existingField.isMappingAllowed) {
      return {
        columnType: 'field',
        fieldName: null,
        fieldLabel: '',
        fieldType: '',
      };
    }
    return {
      columnType: 'field',
      fieldName: existingField.name,
      fieldLabel: existingField.label,
      fieldType: existingField.type,
    };
  }

  if (isCreationDisabled) {
    return {
      columnType: 'field',
      fieldName: null,
      fieldLabel: '',
      fieldType: '',
    };
  }

  return {
    columnType: 'field',
    fieldName: null,
    fieldLabel: columnName || 'New field',
    fieldType: 'Text',
  };
};

const defaultArdoqOidMapping = (): IndexFree<ArdoqOidMapping> => ({
  columnType: 'ardoq-oid',
  duplicates: 'error',
});

const defaultComponentKeyMapping = (): IndexFree<ComponentKeyMapping> => ({
  columnType: 'component-key',
  duplicates: 'error',
});

const defaultComponentTypeMapping = (): IndexFree<ComponentTypeMapping> => ({
  columnType: 'component-type',
});

const defaultReferenceTypeMapping = (): IndexFree<ReferenceTypeMapping> => ({
  columnType: 'reference-type',
});

const defaultDescriptionMapping = (): IndexFree<DescriptionMapping> => ({
  columnType: 'description',
});

const defaultDisplayTextMapping = (): IndexFree<DisplayTextMapping> => ({
  columnType: 'display-text',
});

export const defaultParentMapping = (): IndexFree<PathParentMapping> => ({
  columnType: 'parent',
  parentFormat: 'path',
  parentPathSeparator: null,
  missingComponents: MissingParentComponentsStrategy.ERROR,
});

const defaultRootReferenceMapping = (): IndexFree<
  PathReferenceFormat & RootReferenceMapping
> => ({
  columnType: 'root-reference',
  referenceFormat: 'path',
  referencePathSeparator: '::',
  referenceItemSeparator: ',',
  missingComponents: MissingComponentsStrategy.ERROR,
});

const defaultTargetReferenceMapping = (): IndexFree<
  PathReferenceFormat & TargetReferenceMapping
> => ({
  columnType: 'target-reference',
  referenceFormat: 'path',
  referencePathSeparator: '::',
  referenceItemSeparator: ',',
  missingComponents: MissingComponentsStrategy.ERROR,
});

const defaultTagsMapping = (): IndexFree<TagsMapping> => ({
  columnType: 'tags',
  tagsSeparator: ',',
});
